import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';

function TimesheetAttestationDrawer({ timesheetAttestation, onSaved, onClose, onDeleted }) {
  const title = `${timesheetAttestation?.id ? 'Edit' : 'New'} Timesheet Attestation`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [hasDependencies, setHasDependencies] = useState(true);

  useEffect(() => {
    if (!timesheetAttestation?.id) {
      setHasDependencies(false);
      return;
    }

    (async function fetchData() {
      const dependencies = await api.www
        .workspaces(workspace.id)
        .timesheetAttestations(timesheetAttestation.id)
        .hasDependencies();
      setHasDependencies(dependencies.data);
    })();
  }, [api, timesheetAttestation, workspace]);

  function handleClose() {
    if (onClose) onClose();
  }

  const initialValues = mergeValues({ name: '', attestationText: '' }, timesheetAttestation);

  return (
    <Drawer
      isOpen
      title={title}
      byline="Custom Data"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values, { resetForm }) {
          try {
            form.submit(formRef.current.status.action);

            const body = emptyStringToNull(values);

            const { data } = await api.www
              .workspaces(workspace.id)
              .timesheetAttestations(timesheetAttestation?.id)
              .upsert(body);

            await onSaved(data);
            form.save(formRef.current.status.action);

            switch (formRef.current.status.action) {
              case 'new':
                resetForm();
                toast.success('Timesheet Attestation successfully saved.');
                break;
              case 'close':
                closeDrawer();
                break;
            }

            form.done();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>
              Are you sure that you want to delete this Timesheet Attestation?
            </DeleteConfirmation>
          ));
          if (!confirm) return;

          try {
            await api.www.workspaces(workspace.id).timesheetAttestations(timesheetAttestation.id).delete();
            await onDeleted();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              name: Yup.string().label('Name').max(255).required().trim(),
              attestationText: Yup.string().label('Attestation Text').max(10000).required().trim(),
            })}>
            {(formik) => {
              const submit = async (action) => {
                formik.setStatus({ action });
                formik.submitForm();
              };

              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                    </Form.Control>
                    <Form.Control>
                      <Field.TextArea
                        name="attestationText"
                        placeholder="Attestation Text"
                        maxLength={10000}
                        rows={7}
                      />
                    </Form.Control>
                  </Form.Section>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Drawer.Actions>
                    {timesheetAttestation.id && !hasDependencies && (
                      <DeleteButton onClick={handleDelete}>Delete</DeleteButton>
                    )}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      {!timesheetAttestation.id && (
                        <ActionButton
                          isLoading={isSubmitting === 'new'}
                          ok={saved === 'new'}
                          type="submit"
                          onClick={() => submit('new')}>
                          Save &amp; New
                        </ActionButton>
                      )}

                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        type="submit"
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default TimesheetAttestationDrawer;
